import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import SafeImage from "../SafeImage";
import ProfileAvatarLink from "./ProfileAvatarLink";
import { getNicheMeta } from "../niche/nicheMeta";

const STROKE = 1.8;

const SUB_TABS = [
  { id: "all", label: "All", icon: "grid" },
  { id: "first100", label: "First 100", icon: "star" },
  { id: "unlocked", label: "Unlocked", icon: "lock" },
  { id: "saved", label: "Saved", icon: "bookmark" },
];

function TabIcon({ type }) {
  if (type === "star") {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    );
  }
  if (type === "lock") {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
      </svg>
    );
  }
  if (type === "bookmark") {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />
      </svg>
    );
  }
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function CollectionCard({ item }) {
  const dropId = item.momentId || item.postId || item.dropId || item.id;
  const hasFirst100 = item.type === "firstview" || item.rank != null;
  const rank = item.rank;
  const paidAmount = item.amountPaid != null ? Number(item.amountPaid) : null;
  const niche = getNicheMeta(item.creatorNiche || item.niche);

  return (
    <Link to={`/drop/${dropId}`} className="up-pc-card">
      <div className="up-pc-thumb">
        <SafeImage src={item.mediaUrl || item.thumbnailUrl} alt={item.caption || item.title || "Drop"} className="up-pc-img" fallbackText="" />
        <div className="up-pc-shade" />
        {hasFirst100 && rank != null && (
          <>
            <span className="up-pc-ribbon">First 100</span>
            <div className="up-pc-f100-num">
              <div className="up-pc-f100-rank">#{rank}</div>
              <div className="up-pc-f100-of">of 100</div>
            </div>
          </>
        )}
        {!hasFirst100 && paidAmount != null && paidAmount > 0 && (
          <span className="up-pc-price-tag">Unlocked · ${paidAmount.toFixed(2)}</span>
        )}
        <div className="up-pc-body">
          <div className="up-pc-creator">
            {item.creatorId && (
              <ProfileAvatarLink
                userId={item.creatorId}
                isCreator
                handle={item.creatorUsername || item.creatorHandle}
                src={item.creatorAvatar}
                alt={item.creatorUsername || item.creatorHandle || "Creator"}
                className="up-pc-av-link"
                imgClassName="up-pc-av"
                size="sm"
                onClick={(e) => e.stopPropagation()}
              />
            )}
            {!item.creatorId && item.creatorAvatar && (
              <SafeImage src={item.creatorAvatar} alt="" className="up-pc-av" fallbackText="" />
            )}
            <span className="up-pc-creator-name">@{item.creatorUsername || item.creatorHandle || "creator"}</span>
          </div>
          <div className="up-pc-title-text">{item.caption || item.title || "Untitled drop"}</div>
          <div className="up-pc-meta">
            {niche.label}
            {item.type === "saved" && " · Saved"}
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function PrivateCollectionTab({ userId, isOwnProfile, isPublicView = false }) {
  const canView = isOwnProfile || isPublicView;
  const [activeTab, setActiveTab] = useState("all");
  const [savedItems, setSavedItems] = useState([]);
  const [unlockedItems, setUnlockedItems] = useState([]);
  const [firstViewItems, setFirstViewItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId || !canView) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [savesSnap, unlockedSnap, firstViewsSnap] = await Promise.all([
          getDocs(query(collection(db, "saves"), where("userId", "==", userId))),
          getDocs(query(collection(db, "unlocked_content"), where("userId", "==", userId))),
          getDocs(query(collection(db, "first_views"), where("userId", "==", userId))),
        ]);

        if (cancelled) return;

        setSavedItems(
          savesSnap.docs
            .map((d) => ({ id: d.id, type: "saved", ...d.data() }))
            .sort((a, b) => (b.savedAt?.seconds || 0) - (a.savedAt?.seconds || 0))
        );
        setUnlockedItems(
          unlockedSnap.docs
            .map((d) => ({ id: d.id, type: "unlocked", ...d.data() }))
            .sort((a, b) => (b.unlockedAt?.seconds || 0) - (a.unlockedAt?.seconds || 0))
        );
        setFirstViewItems(
          firstViewsSnap.docs
            .map((d) => ({ id: d.id, type: "firstview", ...d.data() }))
            .sort((a, b) => (a.rank || 999) - (b.rank || 999))
        );
      } catch (err) {
        console.error("Private collection load error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [userId, canView]);

  const allItems = useMemo(() => {
    const map = new Map();
    [...firstViewItems, ...unlockedItems, ...savedItems].forEach((item) => {
      const key = item.momentId || item.postId || item.id;
      const existing = map.get(key);
      if (!existing) {
        map.set(key, item);
        return;
      }
      if (item.type === "firstview" || item.rank != null) {
        map.set(key, { ...existing, ...item, type: "firstview", rank: item.rank ?? existing.rank });
      } else if (item.type === "unlocked" && existing.type !== "firstview") {
        map.set(key, { ...existing, ...item });
      }
    });
    return Array.from(map.values());
  }, [firstViewItems, unlockedItems, savedItems]);

  const visibleItems = useMemo(() => {
    if (activeTab === "first100") return firstViewItems;
    if (activeTab === "unlocked") return unlockedItems;
    if (activeTab === "saved") return savedItems;
    return allItems;
  }, [activeTab, allItems, firstViewItems, unlockedItems, savedItems]);

  const counts = {
    all: allItems.length,
    first100: firstViewItems.length,
    unlocked: unlockedItems.length,
    saved: savedItems.length,
  };

  if (!canView) {
    return null;
  }

  if (loading) {
    return <div className="up-pc-loading">Loading your collection…</div>;
  }

  return (
    <div className="up-pc-wrap">
      <div className="up-pc-stats-row">
        <div className="up-pcs"><div className="up-pcs-num">{counts.all}</div><div className="up-pcs-lbl">Total items</div></div>
        <div className="up-pcs"><div className="up-pcs-num up-pcs-num--gold">{counts.first100}</div><div className="up-pcs-lbl">First 100 unlocks</div></div>
        <div className="up-pcs"><div className="up-pcs-num">{counts.unlocked}</div><div className="up-pcs-lbl">Purchased</div></div>
        <div className="up-pcs"><div className="up-pcs-num">{counts.saved}</div><div className="up-pcs-lbl">Saved for later</div></div>
      </div>

      <div className="up-pc-tabs">
        {SUB_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`up-pct${activeTab === tab.id ? " up-pct--active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <TabIcon type={tab.icon} />
            {tab.label}
            <span className="up-pct-count">{counts[tab.id]}</span>
          </button>
        ))}
      </div>

      {visibleItems.length === 0 ? (
        <div className="up-pc-empty">
          <div className="up-pc-empty-icon">
            <TabIcon type={SUB_TABS.find((t) => t.id === activeTab)?.icon || "grid"} />
          </div>
          <h3 className="up-pc-empty-title">Nothing here yet</h3>
          <p className="up-pc-empty-sub">
            {activeTab === "first100"
              ? "Be among the first 100 viewers on a drop to earn permanent access."
              : activeTab === "unlocked"
                ? "Paid unlocks appear here automatically."
                : activeTab === "saved"
                  ? "Bookmark drops to save them for later."
                  : "Your saved, unlocked, and first-100 drops will show up here."}
          </p>
          <Link to="/gallery" className="up-pc-empty-cta">Browse drops</Link>
        </div>
      ) : (
        <div className="up-pc-grid">
          {visibleItems.map((item) => (
            <CollectionCard key={`${item.type}-${item.id}`} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}
